import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE } from '../utils/api';
import './HistoricData.css';
import './GroupSelector.css';

/**
 * GroupSelector - Handles group and battery selection for role 2 (admin) users.
 *
 * Single unified view:
 *   - Fetches ALL groups the user can access from /api/admin/getGroups
 *   - Live search/filter by group name
 *   - Click a group card to expand its batteries
 *   - "Find by Device ID" fallback section at the bottom
 *
 * Props: { authData, onDeviceSelected, onGroupSelected }
 */
function GroupSelector({ authData, onDeviceSelected, onGroupSelected }) {
  // ── Group list ────────────────────────────────────────────────────────────
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState(null);

  // ── Group search ──────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');

  // ── Selected group / vehicles ─────────────────────────────────────────────
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehiclesError, setVehiclesError] = useState(null);

  // ── Device ID fallback ────────────────────────────────────────────────────
  const [showDeviceIdSection, setShowDeviceIdSection] = useState(false);
  const [deviceIdInput, setDeviceIdInput] = useState('');
  const [loadingDevice, setLoadingDevice] = useState(false);
  const [deviceError, setDeviceError] = useState(null);
  const [deviceResult, setDeviceResult] = useState(null);

  // ── Fetch all accessible groups on mount ──────────────────────────────────
  useEffect(() => {
    const fetchGroups = async () => {
      if (!authData?.token) return;
      setLoadingGroups(true);
      setGroupsError(null);
      try {
        const url = `${API_BASE}/api/admin/getGroups?api_access_token=${authData.token}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setGroups(data.groups || []);
      } catch (err) {
        setGroupsError(err.message);
        console.error('Error fetching groups:', err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [authData]);

  // ── Filtered group list based on search ───────────────────────────────────
  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(g => g.groupName?.toLowerCase().includes(q));
  }, [groups, searchQuery]);

  // ── Fetch vehicles when a group is selected ───────────────────────────────
  const handleSelectGroup = async (group) => {
    if (selectedGroupId === group._id) {
      // Toggle: collapse if already open
      setSelectedGroupId('');
      setSelectedGroupName('');
      setVehicles([]);
      return;
    }
    setSelectedGroupId(group._id);
    setSelectedGroupName(group.groupName);
    setVehicles([]);
    setVehiclesError(null);
    setLoadingVehicles(true);
    try {
      const url = `${API_BASE}/api/admin/getGroupVehicles?groupId=${group._id}&api_access_token=${authData.token}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const fetched = data.vehicles || [];
      setVehicles(fetched);
      if (onGroupSelected && fetched.length > 0) {
        onGroupSelected(fetched.map(v => ({
          deviceId: v.deviceId,
          vehicleId: v.vehicleId,
          _id: v._id,
          carProfile: v.carProfile || null
        })));
      }
    } catch (err) {
      setVehiclesError(err.message);
      console.error('Error fetching vehicles:', err);
    } finally {
      setLoadingVehicles(false);
    }
  };

  // ── Select a vehicle from the expanded group ──────────────────────────────
  const handleSelectVehicle = (vehicle) => {
    const allDevices = vehicles.map(v => ({
      deviceId: v.deviceId,
      vehicleId: v.vehicleId,
      _id: v._id,
      carProfile: v.carProfile || null
    }));
    onDeviceSelected({
      deviceId: vehicle.deviceId,
      vehicleId: vehicle.vehicleId,
      _id: vehicle._id,
      carProfile: vehicle.carProfile || null
    }, allDevices);
  };

  // ── Device ID fallback lookup ─────────────────────────────────────────────
  const handleDeviceLookup = async () => {
    const deviceId = deviceIdInput.trim();
    if (!deviceId) { setDeviceError('Please enter a Device ID.'); return; }
    setLoadingDevice(true);
    setDeviceError(null);
    setDeviceResult(null);
    try {
      const url = `${API_BASE}/api/admin/getDeviceStatus?api_access_token=${authData.token}&deviceId=${deviceId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status} – device not found or access denied`);
      const data = await response.json();
      const vehicleId = data?.vehicle?.vehicleId || data?.vehicleId || deviceId;
      const carProfile = data?.vehicle?.carProfile || data?.carProfile || null;
      const _id = data?.vehicle?._id || data?._id || deviceId;
      setDeviceResult({ deviceId, vehicleId, _id, carProfile });
    } catch (err) {
      setDeviceError(err.message);
    } finally {
      setLoadingDevice(false);
    }
  };

  const handleSelectDeviceResult = () => {
    if (!deviceResult) return;
    onDeviceSelected(deviceResult, [deviceResult]);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="historic-data-view">
      <h2>Select Battery Asset</h2>
      <p className="subtitle">Search for a group, then select a battery device</p>

      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <div className="gs-search-bar">
        <svg className="gs-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          id="gs-search-input"
          type="text"
          className="gs-search-input"
          placeholder={loadingGroups ? 'Loading groups…' : `Search among ${groups.length} group${groups.length !== 1 ? 's' : ''}…`}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          disabled={loadingGroups}
        />
        {searchQuery && (
          <button className="gs-search-clear" onClick={() => setSearchQuery('')} title="Clear">✕</button>
        )}
      </div>

      {groupsError && (
        <div className="error-message"><strong>Error loading groups:</strong> {groupsError}</div>
      )}

      {/* ── Group list ──────────────────────────────────────────────────── */}
      {loadingGroups ? (
        <div className="gs-loading">Loading groups…</div>
      ) : filteredGroups.length === 0 ? (
        <div className="no-data">
          <p>{searchQuery ? `No groups matching "${searchQuery}".` : "Your account doesn't have group access — showing your assigned devices instead."}</p>
        </div>
      ) : (
        <div className="gs-group-list">
          {filteredGroups.map(group => {
            const isOpen = selectedGroupId === group._id;
            return (
              <div key={group._id} className={`gs-group-card${isOpen ? ' gs-group-card--open' : ''}`}>
                {/* Group header – click to expand */}
                <button
                  className="gs-group-header"
                  onClick={() => handleSelectGroup(group)}
                >
                  <div className="gs-group-info">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <span className="gs-group-name">{group.groupName}</span>
                  </div>
                  <svg
                    className={`gs-chevron${isOpen ? ' gs-chevron--open' : ''}`}
                    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {/* Expanded: vehicle list */}
                {isOpen && (
                  <div className="gs-vehicle-panel">
                    {loadingVehicles ? (
                      <div className="gs-loading">Loading batteries…</div>
                    ) : vehiclesError ? (
                      <div className="error-message"><strong>Error:</strong> {vehiclesError}</div>
                    ) : vehicles.length === 0 ? (
                      <div className="no-data"><p>No batteries found in this group.</p></div>
                    ) : (
                      <div className="table-container">
                        <table className="historic-table">
                          <thead>
                            <tr>
                              <th>Vehicle ID</th>
                              <th>Device ID</th>
                              <th>Model</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vehicles.map(vehicle => (
                              <tr key={vehicle._id || vehicle.deviceId}>
                                <td>{vehicle.vehicleId || '—'}</td>
                                <td><code>{vehicle.deviceId}</code></td>
                                <td>{vehicle.carProfile?.model || 'Unknown'}</td>
                                <td>
                                  <button
                                    className="select-button"
                                    onClick={() => handleSelectVehicle(vehicle)}
                                  >
                                    Select
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Device ID fallback ───────────────────────────────────────────── */}
      <div className="gs-device-fallback">
        <button
          className="gs-fallback-toggle"
          onClick={() => { setShowDeviceIdSection(s => !s); setDeviceError(null); setDeviceResult(null); }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="6" width="22" height="12" rx="2" ry="2"/>
            <line x1="23" y1="13" x2="23" y2="11"/>
          </svg>
          {showDeviceIdSection ? 'Hide device ID lookup' : 'Find by Device ID instead'}
          <svg
            className={`gs-chevron${showDeviceIdSection ? ' gs-chevron--open' : ''}`}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {showDeviceIdSection && (
          <div className="gs-device-body">
            <p className="gs-device-hint">
              Enter the Device ID (IMEI / tracker ID) directly if you know it.
            </p>
            <div className="gs-manual-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="gs-device-id-input">Device ID:</label>
                <input
                  id="gs-device-id-input"
                  type="text"
                  placeholder="e.g. FM353691848531173"
                  value={deviceIdInput}
                  onChange={e => { setDeviceIdInput(e.target.value); setDeviceError(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleDeviceLookup()}
                  disabled={loadingDevice}
                />
              </div>
              <button
                id="gs-device-lookup-btn"
                className="select-button gs-lookup-btn"
                onClick={handleDeviceLookup}
                disabled={loadingDevice || !deviceIdInput.trim()}
              >
                {loadingDevice ? 'Looking up…' : 'Look Up'}
              </button>
            </div>

            {deviceError && (
              <div className="error-message"><strong>Error:</strong> {deviceError}</div>
            )}

            {deviceResult && (
              <div className="results">
                <div className="results-header"><h3>Device Found</h3></div>
                <div className="table-container">
                  <table className="historic-table">
                    <thead>
                      <tr>
                        <th>Vehicle ID</th>
                        <th>Device ID</th>
                        <th>Model</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{deviceResult.vehicleId || '—'}</td>
                        <td><code>{deviceResult.deviceId}</code></td>
                        <td>{deviceResult.carProfile?.model || 'Unknown'}</td>
                        <td>
                          <button className="select-button" onClick={handleSelectDeviceResult}>
                            Select
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupSelector;
